<template>
  <main>
    <div class="report-header">
      <h1 class="page-header">impact reports run</h1>
      <a href="/reports/impact/new" class="button larger">new report</a>
    </div>
    <div class="report-progress">
      <h4 class="report-title">in progress</h4>
      <div v-if="inProgressReports.length > 0">
        <sb-table
          :columns="tableColumns"
          :rows="inProgressReports"
          class="sb-table striped">
          <template #cell="{ row, column }">
            <div @click="viewReport(row.id)" :class="`name-${column.key}-${row.id}`">
              {{ generateLabel(row[column.key]) }}
              <Tooltip
                v-if="showTooltip(row[column.key])"
                :text="generateLabel(row[column.key])"
                :target="`.name-${column.key}-${row.id}`"
                :show-delay="500"
                custom-class="impactReport-tooltip"
              />
            </div>
          </template>
        </sb-table>
      </div>
      <div v-else class="no-reports">
        <p>no reports currently in progress</p>
      </div>
    </div>
    <div class="report-completed">
      <h4 class="report-title">reports</h4>
      <div v-if="completedReports.length > 0">
        <sb-table
          :columns="tableColumns"
          :rows="completedReports"
          class="sb-table striped">
          <template #cell="{ row, column }">
            <div @click="viewReport(row.id)" :class="`name-${column.key}-${row.id}`">
              {{ generateLabel(row[column.key]) }}
              <Tooltip
                v-if="showTooltip(row[column.key])"
                :text="generateLabel(row[column.key])"
                :target="`.name-${column.key}-${row.id}`"
                :show-delay="500"
                custom-class="impactReport-tooltip"
              />
            </div>
          </template>
        </sb-table>
      </div>
      <div v-else class="no-reports">
        <p>no reports have been completed</p>
      </div>
    </div>
    <div class="report-pagination">
      <sb-pagination :page.sync="currentPage" :pages="totalPages" gap="2" showFirstLast="true" showEllipsis="true" class="sb-pagination"></sb-pagination>
    </div>
  </main>
</template>

<script>
import { ReportAPI } from "../../../services/api/Reports";
import { Tooltip } from 'uiv';
import { mapState } from "vuex";
import { format, utcToZonedTime } from "date-fns-tz";

export default {
  name: 'ImpactReport',
  components: {
    Tooltip
  },
  data() {
    return {
      tableColumns: [
        {
          key: 'eventType',
          label: 'event type'
        },
        {
          key: 'event',
          label: 'event'
        },
        {
          key: 'dateCreated',
          label: 'date created'
        }
      ],
      inProgressReports: [],
      completedReports: [],
      totalPages: null,
      currentPage: 0
    }
  },
  computed: {
    ...mapState(['currentMerchant'])
  },
  watch: {
    currentPage(page) {
      this.fetchInProgressReports()
      this.fetchCompletedReports(page)
    }
  },
  async created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT')
    this.fetchInProgressReports()
    this.fetchCompletedReports(this.currentPage)
  },
  methods: {
    async fetchInProgressReports() {
      const data = await ReportAPI.fetchInProgress()
      this.inProgressReports = this.mapReportData(data.reports)
    },
    async fetchCompletedReports(pageNumber) {
      const data = await ReportAPI.fetchCompleted(pageNumber)
      this.totalPages = data.pagination.total_pages
      this.completedReports = this.mapReportData(data.reports)
    },
    mapReportData(reports) {
      return reports.map(item => {
        const minMaxValues = `${item.report.input_data_labels.event_range_min} - ${item.report.input_data_labels.event_range_max}`
        const eventValue = item.report.input_data_labels.event_range_min ? minMaxValues : item.report.input_data_labels.event_value

        return {
          id: item.report.id,
          eventType: item.report.input_data_labels.event_type,
          event: eventValue,
          dateCreated: this.formatCreatedAtDate(item.report.created_at),
          downloadStatus: 'generate'
        }
      })
    },
    formatCreatedAtDate(date) {
      const utcToLocalDate = utcToZonedTime(new Date(date), this.currentMerchant.merchant_time_zone)
      return format(utcToLocalDate, `MM/dd/yyyy h:mm aaa`)
    },
    viewReport(reportId) {
      window.location.pathname = `/reports/impact/${reportId}`
    },
    generateLabel(label) {
      return label?.toLowerCase() || 'n/a'
    },
    showTooltip(label) {
      return label?.length > 25
    }
  }
}
</script>

<style scoped>
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 42px 0;
}
.report-header .page-header {
  margin-bottom: 0 !important;
}
.report-header .button {
  margin: 0;
}

.report-title {
  text-align: center;
  color: #4a4a4a;
  font-family: "AileronLight";
}

.no-reports {
  text-align: center;
}

.report-pagination {
  max-width: 350px;
  margin: 0 auto;
}

.loading-icon >>> .sb-loading-icon {
  display: flex;
  width: 18px;
  margin: 0 auto;
}

.sb-pagination >>> .index.icon {
  font-size: 16px;
  color: #0a0a0a;
}

.sb-pagination >>> .index.icon.active {
  background: #cae6fe;
  padding: 8px 12px;
  color: #0a0a0a;
  font-size: 16px;
}

.sb-pagination >>> .icon, .sb-pagination >>> .next.icon,.sb-pagination >>> .prev.icon {
  font-size: 16px;
  color: #0a0a0a;
}

.sb-pagination >>> .next.icon.disabled, .sb-pagination >>> .prev.icon.disabled {
  font-size: 16px;
  color: #adadad;
  display: none;
}

.sb-table {
  margin: 14px 0;
}

.sb-table >>> .grid-content:hover {
  background: #128ff9 !important;
  color: #fff;
  cursor: pointer;
}

.sb-table >>> .grid-content:hover a {
  color: #fff;
}

.striped >>> .grid-content:nth-child(even) {
  background: #cae6fe;
}

.sb-table  >>> .grid-header > div,
.sb-table  >>> .grid-content > div {
  padding: 4px 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.sb-table  >>> .grid-header > div {
  font-weight: bold;
  color: #4a4a4a;
}
</style>